/**
 * Clasificación de etiquetas Kommo: Meta vs No Meta + mapa etiqueta → campaña Ads.
 * @module-isolated analytics-config-origins
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchMarketingAnalytics } from '../../../services/analytics-api.js';
import { getIdToken } from '../../../services/firebase-auth.js';
import { loadOriginTags, persistOriginTags } from '../services/originTagsService.js';

function sortTags(list) {
  return [...list].sort((a, b) => {
    const ao = Number(a.order) || 0;
    const bo = Number(b.order) || 0;
    if (ao !== bo) return ao - bo;
    return String(a.tagName || '').localeCompare(String(b.tagName || ''), 'es');
  });
}

function metaLabel(v) {
  if (v === true) return 'Meta';
  if (v === false) return 'No Meta';
  return 'Sin clasificar';
}

function isSinEtiquetaTag(t) {
  if (!t) return false;
  if (t.isNoTag === true) return true;
  const k = String(t.tagName || '')
    .trim()
    .toLowerCase();
  return k === 'sin etiqueta' || k === '__none__' || k === 'sin_etiqueta';
}

function ymdDaysAgo(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}

function ymdToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + day;
}

export default function OriginTagsPanel() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [tags, setTags] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState({ msg: '', level: '' });
  const [dirty, setDirty] = useState(false);
  const [filter, setFilter] = useState('all'); // all | meta | other | unclassified
  const [metaCampaigns, setMetaCampaigns] = useState([]);

  const load = useCallback(async (opts) => {
    const sync = !!(opts && opts.syncFromLeads);
    setBusy(sync ? 'sync' : 'load');
    setStatus({ msg: '', level: '' });
    try {
      const res = await loadOriginTags({ syncFromLeads: sync });
      if (!res || res.success === false) {
        throw new Error((res && res.error) || 'No se pudieron cargar las etiquetas.');
      }
      setTags(
        sortTags(
          (res.items || []).map((t) => ({
            ...t,
            metaCampaignId: t.metaCampaignId || '',
            metaCampaignName: t.metaCampaignName || '',
            isNoTag: isSinEtiquetaTag(t)
          }))
        )
      );
      setStats(res.stats || null);
      setDirty(false);
      const warn = (res.warnings || []).join(' ');
      setStatus({
        msg: warn || 'Listo · ' + ((res.items || []).length) + ' etiqueta(s).',
        level: warn ? 'warn' : 'ok'
      });
    } catch (err) {
      setStatus({
        msg: (err && err.message) || 'Error al cargar orígenes.',
        level: 'bad'
      });
    } finally {
      setBusy('');
      setLoading(false);
    }
  }, []);

  const loadMetaCampaigns = useCallback(async () => {
    try {
      const token = await getIdToken(false);
      const raw = await fetchMarketingAnalytics(
        { dateFrom: ymdDaysAgo(90), dateTo: ymdToday() },
        token
      );
      const rows =
        raw && raw.table && Array.isArray(raw.table.rows) ? raw.table.rows : [];
      const byId = {};
      rows.forEach((r) => {
        const id = String((r && (r.id || r.campaignId)) || '').trim();
        const name = String((r && (r.campana || r.name || r.campaignName)) || '').trim();
        if (!id && !name) return;
        const key = id || name.toLowerCase();
        if (!byId[key]) byId[key] = { id, name: name || id };
      });
      setMetaCampaigns(
        Object.keys(byId)
          .map((k) => byId[k])
          .sort((a, b) => String(a.name).localeCompare(String(b.name), 'es'))
      );
    } catch (_err) {
      setMetaCampaigns([]);
    }
  }, []);

  useEffect(() => {
    load({ syncFromLeads: true });
    loadMetaCampaigns();
  }, [load, loadMetaCampaigns]);

  const visible = useMemo(() => {
    if (filter === 'meta') return tags.filter((t) => t.isMeta === true);
    if (filter === 'other') return tags.filter((t) => t.isMeta === false);
    if (filter === 'unclassified') return tags.filter((t) => t.isMeta !== true && t.isMeta !== false);
    return tags;
  }, [tags, filter]);

  function setMeta(tagName, value) {
    setTags((prev) =>
      prev.map((t) =>
        String(t.tagName) === String(tagName)
          ? {
              ...t,
              isMeta: value,
              isOrigin: value === null ? t.isOrigin : true,
              metaCampaignId: value === true ? t.metaCampaignId || '' : '',
              metaCampaignName: value === true ? t.metaCampaignName || '' : ''
            }
          : t
      )
    );
    setDirty(true);
  }

  function setIsOrigin(tagName, value) {
    setTags((prev) =>
      prev.map((t) => (String(t.tagName) === String(tagName) ? { ...t, isOrigin: !!value } : t))
    );
    setDirty(true);
  }

  function setCampaignFields(tagName, patch) {
    setTags((prev) =>
      prev.map((t) =>
        String(t.tagName) === String(tagName) ? { ...t, ...patch } : t
      )
    );
    setDirty(true);
  }

  function applyCampaignPick(tagName, campaignId) {
    const id = String(campaignId || '').trim();
    if (!id) {
      setCampaignFields(tagName, { metaCampaignId: '', metaCampaignName: '' });
      return;
    }
    const found = metaCampaigns.find((c) => String(c.id) === id);
    setCampaignFields(tagName, {
      metaCampaignId: id,
      metaCampaignName: (found && found.name) || ''
    });
  }

  async function runSave() {
    setBusy('save');
    setStatus({ msg: 'Guardando…', level: '' });
    try {
      const payload = tags.map((t, i) => ({
        tagName: t.tagName,
        isMeta: t.isMeta === true ? true : t.isMeta === false ? false : null,
        isOrigin: t.isOrigin !== false,
        order: t.order != null ? t.order : i + 1,
        active: t.active !== false,
        metaCampaignId: t.isMeta === true ? String(t.metaCampaignId || '').trim() : '',
        metaCampaignName: t.isMeta === true ? String(t.metaCampaignName || '').trim() : ''
      }));
      const res = await persistOriginTags(payload, {});
      if (!res || res.success === false) {
        throw new Error((res && res.error) || 'No se pudo guardar.');
      }
      setTags(
        sortTags(
          (res.items || payload).map((t) => ({
            ...t,
            metaCampaignId: t.metaCampaignId || '',
            metaCampaignName: t.metaCampaignName || ''
          }))
        )
      );
      setDirty(false);
      setStatus({
        msg: res.message || 'Guardado · ' + (res.saved || payload.length) + ' etiqueta(s).',
        level: 'ok'
      });
      await load({ syncFromLeads: false });
    } catch (err) {
      setStatus({
        msg: (err && err.message) || 'Error al guardar.',
        level: 'bad'
      });
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="analytics-stages-panel analytics-origins-panel">
      <p className="analytics-section-note">
        Etiquetas de Kommo como origen del lead. Marcá cuáles son <strong>Meta</strong> (inversión)
        y cuáles <strong>No Meta</strong> (cada una queda como origen aparte: PatagoLand, portal,
        etc.). En etiquetas Meta podés fijar la <strong>campaña Ads</strong> cuando Kommo no trae
        Campaign ID (p. ej. Lead Ads con tag críptico). La fila especial{' '}
        <strong>Sin Etiqueta</strong> aplica a leads de Kommo que no tienen ninguna tag — también
        podés clasificarla y mapearla a una campaña. Las demás sin clasificar no cuentan como origen.
      </p>

      <div className="analytics-toolbar" style={{ flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <button
          type="button"
          className="analytics-btn analytics-btn-ghost"
          disabled={!!busy}
          onClick={() => load({ syncFromLeads: true })}
        >
          {busy === 'sync' ? 'Sincronizando…' : 'Sincronizar etiquetas desde Leads'}
        </button>
        <button
          type="button"
          className="analytics-btn analytics-btn-ghost"
          disabled={!!busy}
          onClick={loadMetaCampaigns}
        >
          Actualizar campañas Meta
        </button>
        <button
          type="button"
          className="analytics-btn"
          disabled={!!busy || !dirty}
          onClick={runSave}
        >
          {busy === 'save' ? 'Guardando…' : 'Guardar clasificación'}
        </button>
        <span
          className={'analytics-stages-status' + (status.level ? ' is-' + status.level : '')}
          aria-live="polite"
        >
          {status.msg}
        </span>
      </div>

      {stats ? (
        <p className="analytics-section-note" style={{ marginTop: 0 }}>
          Total {stats.total} · Meta {stats.meta} · No Meta {stats.other} · Sin clasificar{' '}
          {stats.unclassified}
          {metaCampaigns.length
            ? ' · Campañas Meta (90d): ' + metaCampaigns.length
            : ''}
        </p>
      ) : null}

      <div className="leads-subnav config-tabs" style={{ marginBottom: 12 }}>
        {[
          { id: 'all', label: 'Todas' },
          { id: 'meta', label: 'Meta' },
          { id: 'other', label: 'No Meta' },
          { id: 'unclassified', label: 'Sin clasificar' }
        ].map((f) => (
          <button
            key={f.id}
            type="button"
            className={'leads-subnav-btn' + (filter === f.id ? ' is-active' : '')}
            onClick={() => setFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? <p className="analytics-empty">Cargando…</p> : null}

      {!loading ? (
        <div className="analytics-panel analytics-panel-scroll">
          <table className="analytics-table">
            <thead>
              <tr>
                <th>Etiqueta Kommo</th>
                <th>Clasificación</th>
                <th>Es origen</th>
                <th>Campaña Meta</th>
              </tr>
            </thead>
            <tbody>
              {!visible.length ? (
                <tr>
                  <td colSpan={4} className="analytics-empty">
                    Sin etiquetas. Tocá «Sincronizar etiquetas desde Leads» (requiere sync Kommo
                    previo).
                  </td>
                </tr>
              ) : (
                visible.map((t) => (
                  <tr key={t.tagName} className={isSinEtiquetaTag(t) ? 'is-special-origin' : ''}>
                    <td>
                      <strong>{t.tagName}</strong>
                      {isSinEtiquetaTag(t) ? (
                        <span className="analytics-chip" title="Leads Kommo sin ninguna etiqueta">
                          Especial
                        </span>
                      ) : null}
                      <span className="analytics-sub">{metaLabel(t.isMeta)}</span>
                      {t.isMeta === true && (t.metaCampaignId || t.metaCampaignName) ? (
                        <span className="analytics-sub">
                          → {t.metaCampaignName || t.metaCampaignId}
                        </span>
                      ) : null}
                      {isSinEtiquetaTag(t) ? (
                        <div className="analytics-sub">
                          Representa leads sin tag en Kommo (no es una etiqueta real del CRM).
                        </div>
                      ) : null}
                    </td>
                    <td>
                      <select
                        className="analytics-select"
                        value={
                          t.isMeta === true ? 'meta' : t.isMeta === false ? 'other' : 'unset'
                        }
                        disabled={!!busy}
                        onChange={(ev) => {
                          const v = ev.target.value;
                          setMeta(
                            t.tagName,
                            v === 'meta' ? true : v === 'other' ? false : null
                          );
                        }}
                      >
                        <option value="unset">Sin clasificar</option>
                        <option value="meta">Meta (pagado)</option>
                        <option value="other">No Meta</option>
                      </select>
                    </td>
                    <td>
                      <label className="analytics-check">
                        <input
                          type="checkbox"
                          checked={t.isOrigin !== false}
                          disabled={!!busy || t.isMeta == null}
                          onChange={(ev) => setIsOrigin(t.tagName, ev.target.checked)}
                        />{' '}
                        Origen
                      </label>
                    </td>
                    <td>
                      {t.isMeta === true ? (
                        <div className="analytics-origin-campaign-fields">
                          {metaCampaigns.length ? (
                            <select
                              className="analytics-select"
                              value={String(t.metaCampaignId || '')}
                              disabled={!!busy}
                              onChange={(ev) => applyCampaignPick(t.tagName, ev.target.value)}
                              aria-label={'Campaña Meta para ' + t.tagName}
                            >
                              <option value="">— Sin mapa (solo empirico) —</option>
                              {metaCampaigns.map((c) => (
                                <option key={c.id || c.name} value={c.id}>
                                  {c.name}
                                  {c.id ? ' · ' + c.id : ''}
                                </option>
                              ))}
                            </select>
                          ) : null}
                          <input
                            type="text"
                            className="analytics-input"
                            placeholder="Campaign ID"
                            value={String(t.metaCampaignId || '')}
                            disabled={!!busy}
                            onChange={(ev) =>
                              setCampaignFields(t.tagName, {
                                metaCampaignId: ev.target.value
                              })
                            }
                          />
                          <input
                            type="text"
                            className="analytics-input"
                            placeholder="Nombre campaña"
                            value={String(t.metaCampaignName || '')}
                            disabled={!!busy}
                            onChange={(ev) =>
                              setCampaignFields(t.tagName, {
                                metaCampaignName: ev.target.value
                              })
                            }
                          />
                        </div>
                      ) : (
                        <span className="analytics-sub">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
